import React from 'react';

import { ResumeContentContext } from '../../../types';
import { renderCreditInfoBlock, renderCreditInfoBlockWithAddNew } from '../../shared/creditInfoHelpers';

/**
 * Clona el elemento guarantor y le aplica estilos para usuarios logueados
 */
const getGuarantorElementWithStyles = (guarantorElement: React.ReactNode): React.ReactNode => {
  if (React.isValidElement(guarantorElement)) {
    return React.cloneElement(guarantorElement as React.ReactElement<any>, {
      style: {
        ...(guarantorElement as React.ReactElement<any>).props?.style,
        textDecoration: 'none',
        cursor: 'auto',
        display: 'inline',
      },
    });
  }
  return guarantorElement;
};

/**
 * Renderiza el contenido inicial solo con state/guarantor (modo usuario normal)
 */
const renderInitialContent = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  isSomeoneElse: boolean,
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  locationPreposition: 'in' | 'at',
  context: ResumeContentContext
) => (
  <React.Fragment>
    <div data-testid="render-initial-content-user">
      <div className='text-summary'>Show me loan terms for {getGuarantorElementWithStyles(guarantor.element)}
        {steps.state.isSet ? <> {locationPreposition} {steps.state.element}</> : null}
      </div>
      <div className='text-info-borrower' style={{ marginTop: '128px' }} data-testid="render-initial-content-user-credit-info">
        {renderCreditInfoBlock(
          isSomeoneElse,
          guarantor,
          ownershipItems,
          isCreditInfoLocked,
          canUnlockCreditInfo,
          ficoElementResolved,
          propertiesElementResolved,
          unlockCreditInfo,
          context
        )}
      </div>
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con loanType (modo usuario normal)
 */
const renderWithLoanType = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  isSomeoneElse: boolean,
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  locationPreposition: 'in' | 'at',
  context: ResumeContentContext
) => (
  <React.Fragment>
    <div data-testid="render-with-loan-type-user">
      <div className='text-summary'>Show me loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.loanType.element} {locationPreposition} {steps.state.element}</div>
      <div className='text-info-borrower' style={{ marginTop: '128px' }} data-testid="render-with-loan-type-user-credit-info">
        {renderCreditInfoBlock(
          isSomeoneElse,
          guarantor,
          ownershipItems,
          isCreditInfoLocked,
          canUnlockCreditInfo,
          ficoElementResolved,
          propertiesElementResolved,
          unlockCreditInfo,
          context
        )}
      </div>
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con loanOwner (modo usuario normal)
 */
const renderWithLoanOwner = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  isSomeoneElse: boolean,
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  locationPreposition: 'in' | 'at',
  context: ResumeContentContext
) => (
  <React.Fragment>
    <div data-testid="render-with-loan-owner-user">
      <div className='text-summary'>Show me {steps.loanType.element} loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.loanOwner.element} {locationPreposition} {steps.state.element}</div>
      <div className='text-info-borrower' style={{ marginTop: '128px' }} data-testid="render-with-loan-owner-user-credit-info">
        {renderCreditInfoBlock(
          isSomeoneElse,
          guarantor,
          ownershipItems,
          isCreditInfoLocked,
          canUnlockCreditInfo,
          ficoElementResolved,
          propertiesElementResolved,
          unlockCreditInfo,
          context
        )}
      </div>
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con propertyType (modo usuario normal)
 */
const renderWithPropertyType = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-with-property-type-user">
      Show me {steps.loanType.element} loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido compacto (una línea) para modo usuario normal
 */
const renderCompact = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  ficoElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-compact-user">
      Show me {steps.loanType.element} loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. {guarantor.text} has a FICO score of {ficoElementResolved}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido expandido (multilínea) para modo usuario normal
 */
const renderExpanded = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  ficoElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-expanded-user-summary">
      Show me {steps.loanType.element} loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}.
    </div>
    {ficoElementResolved && (ficoElementResolved as any).props?.text !== 'N/A' && (
      <div data-testid="render-expanded-user-fico">
        {guarantor.text} has a FICO score of {ficoElementResolved}
      </div>
    )}
  </React.Fragment>
);

/**
 * Renderiza el contenido completo expandido para modo usuario normal
 */
const renderCompleteExpanded = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  isSomeoneElse: boolean,
  ownershipItems: any[],
  isCreditInfoLocked: boolean,
  canUnlockCreditInfo: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  unlockCreditInfo: () => void,
  locationPreposition: 'in' | 'at',
  context: ResumeContentContext
) => (
  <React.Fragment>
    <div className='text-summary' data-testid="render-complete-expanded-user-summary">
      Show me {steps.loanType.element} loan terms for {getGuarantorElementWithStyles(guarantor.element)} for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
    <div className='text-info-borrower' data-testid="render-complete-expanded-user-credit-info">
      {renderCreditInfoBlockWithAddNew(
        isSomeoneElse,
        guarantor,
        ownershipItems,
        isCreditInfoLocked,
        canUnlockCreditInfo,
        ficoElementResolved,
        propertiesElementResolved,
        unlockCreditInfo,
        context
      )}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido completo en una línea para modo first person
 */
const renderCompleteOneLineFirstPerson = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div className="summary-text-consistent" data-testid="render-complete-one-line-first-person">
      Show me a {steps.loanType.element} loan for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} <span className="no-gap-after">{steps.state.element}.</span> <br className="br-summary" /> {guarantor.text} has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the last 36 months
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido completo en una línea para modo normal
 */
const renderCompleteOneLineNormal = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-complete-one-line-normal">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. {guarantor.text} has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
    </div>
  </React.Fragment>
);

export const userRenderers = {
  renderInitialContent,
  renderWithLoanType,
  renderWithLoanOwner,
  renderWithPropertyType,
  renderCompact,
  renderExpanded,
  renderCompleteExpanded,
  renderCompleteOneLineFirstPerson,
  renderCompleteOneLineNormal,
};

